import React, { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import translationKeys from '@translationKeys'

import placeholderIcon from '@assets/images/search-state-placeholder.svg'
import SearchIcon from '@components/icons/SearchIcon'
import { useAuthContext } from '@modules/auth/hooks/useAuthContext'
import { isP1PlusNotFreeUser } from '@modules/auth/types'
import { Typography } from '@mui/material'

import {
  OperatorBadge,
  OperatorBadgeWrapper,
  OperatorTipsItem,
  OperatorTipsWrapper,
  SearchIconPlaceholderWrapper,
  SearchPlaceholderWrapper,
  SearchTipsItem,
  SearchTipsWrapper,
} from '../index.styles'

const SearchResultPlaceholder = () => {
  const { t } = useTranslation()
  const { user } = useAuthContext()
  const isSubscriber = useMemo(() => isP1PlusNotFreeUser(user?.subscriptionLevel as number), [user])
  const headerTranslationKey = useMemo(
    () =>
      isSubscriber ? translationKeys.SEARCH_PLACEHOLDER_SUBSCRIBER_HEADING : translationKeys.SEARCH_PLACEHOLDER_FREE_HEADING,
    [isSubscriber],
  )
  const searchTips = useMemo(
    () =>
      isSubscriber ? (
        <>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_SUBSCRIBER_TIPS_1)}</SearchTipsItem>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_SUBSCRIBER_TIPS_2)}</SearchTipsItem>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_SUBSCRIBER_TIPS_3)}</SearchTipsItem>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_SUBSCRIBER_TIPS_4)}</SearchTipsItem>
        </>
      ) : (
        <>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_FREE_TIPS_1)}</SearchTipsItem>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_FREE_TIPS_2)}</SearchTipsItem>
          <SearchTipsItem>{t(translationKeys.SEARCH_PLACEHOLDER_FREE_TIPS_3)}</SearchTipsItem>
        </>
      ),
    [isSubscriber, t],
  )

  return (
    <SearchPlaceholderWrapper data-testid="search-placeholder">
      <SearchIconPlaceholderWrapper>
        <SearchIcon fontSize="inherit" data-testid="search-icon" />
      </SearchIconPlaceholderWrapper>
      <Typography variant="h1" maxWidth="339px" textAlign="center" fontWeight={400} lineHeight="111%">
        {t(headerTranslationKey)}
      </Typography>
      <img
        src={placeholderIcon}
        alt={t(translationKeys.SEARCH_PLACEHOLDER_TIPS_HEADING) || ''}
        data-testid="search-placeholder-icon"
      />
      <OperatorTipsWrapper>
        <Typography sx={{ marginBottom: '4px' }} variant="h2">
          {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS)}
        </Typography>
        <OperatorTipsItem>
          <Typography sx={{ flex: '1', textAlign: 'right', letterSpacing: 'normal' }} variant="caption">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_1)}
          </Typography>
          <OperatorBadge>“---”</OperatorBadge>
          <Typography sx={{ flex: '1' }} variant="body1">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_EXAMPLE_1)}
          </Typography>
        </OperatorTipsItem>
        <OperatorTipsItem>
          <Typography sx={{ flex: '1', textAlign: 'right' }} variant="caption">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_2)}
          </Typography>
          <OperatorBadgeWrapper>
            <OperatorBadge>AND</OperatorBadge>
          </OperatorBadgeWrapper>

          <Typography sx={{ flex: '1' }} variant="body1">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_EXAMPLE_2)}
          </Typography>
        </OperatorTipsItem>
        <OperatorTipsItem>
          <Typography sx={{ flex: '1', textAlign: 'right' }} variant="caption">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_3)}
          </Typography>
          <OperatorBadge>NOT</OperatorBadge>
          <Typography sx={{ flex: '1' }} variant="body1">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_EXAMPLE_3)}
          </Typography>
        </OperatorTipsItem>
        <OperatorTipsItem>
          <Typography sx={{ flex: '1', textAlign: 'right' }} variant="caption">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_4)}
          </Typography>
          <OperatorBadgeWrapper>
            <OperatorBadge>OR</OperatorBadge>
          </OperatorBadgeWrapper>
          <Typography sx={{ flex: '1' }} variant="body1">
            {t(translationKeys.SEARCH_PLACEHOLDER_OPERATORS_TIPS_EXAMPLE_4)}
          </Typography>
        </OperatorTipsItem>
      </OperatorTipsWrapper>

      <Typography variant="h2">{t(translationKeys.SEARCH_PLACEHOLDER_TIPS_HEADING)}</Typography>
      <SearchTipsWrapper>{searchTips}</SearchTipsWrapper>
    </SearchPlaceholderWrapper>
  )
}

export default memo(SearchResultPlaceholder)
